import React, { useMemo } from 'react'
import { Form, schema } from '@ggs/forms'
import { useI18n } from '@ggs/i18n'
import { Button, HelpTooltip } from '../../buttons'
import { Box, CircularProgress, Link } from '@mui/material'
import { DataTable, SelectOptionControl } from '../../'
import TablePagination from '@mui/material/TablePagination'
import { useAcademicDataDigest, useInstructorSearch } from '../../../local'
import { size, isEmpty } from 'lodash'
import { addNotification } from '../../../store/actions/ui'
import Typography from '@mui/material/Typography'
import { useDispatch } from '@ggs/store'
import rowsPerPageOptions from './rowPerPageOptions.json'
import './InstructorSearchForm.scss'

/**
 * @typedef {import('mui-datatables').MUIDataTableOptions} MUIDataTableOptions
 * @typedef {import('../../../definitions').CourseSectionsExpListParams} CourseSectionsExpListParams
 */

/**
 * @typedef {Object} InstructorSearchFormProps
 */

/**
 * Instructor search form.
 * @return {JSX.Element}
 */
export function InstructorSearchForm() {
  const { t } = useI18n()
  const { handlers, state } = useInstructorSearch()
  const {
    currentFilters,
    currentPage,
    currentPageIndex,
    loading,
    resultsCount,
    rowsPerPage,
    pageResults,
    pagesIndexOptions,
  } = state
  const dispatch = useDispatch()
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'

  const fieldsDefinition = {
    instructorName: {
      label: t('Instructor'),
      type: schema.Forms.FieldTypes.text,
      afterFieldText: <HelpTooltip message={t('Search the instructor of all UBC Courses')} />,
      variant: 'standard',
    },
    courseSubject: {
      label: t('Course Subject'),
      type: schema.Forms.FieldTypes.text,
      variant: 'standard',
      afterFieldText: (
        <HelpTooltip
          message={t('Enter a course subject to lookup Instructors for. Example ANTH_V.')}
        />
      ),
    },
  }

  /**
   * Handle form submit.
   *
   * @param {CourseSectionsExpListParams} values
   * @param {any} formik
   */
  const onSubmitHandler = (values, formik) => {
    const { setSubmitting } = formik
    const isEmptySearch = isEmpty(values.instructorName) && isEmpty(values.courseSubject)
    if (isEmptySearch) {
      setSubmitting(false)
      dispatch(
        addNotification(
          'instructor_search_failure',
          'error',
          t('Please provide at least one filter criteria: instructor name or course subject.')
        )
      )

      return
    }

    handlers.onSubmitHandler(values, formik)
  }

  const FormInstance = Form.useForm({
    withDraftState: false,
    formId: 'form_instructor__search',
    className: 'form_instructor__search',
    // @ts-ignore
    fields: fieldsDefinition,
    actions: [
      {
        type: 'submit',
        label: t('Search for Instructors'),
        variant: Button.variant.contained,
      },
    ],
    sx: {},
    onSubmit: onSubmitHandler,
  })

  const startResultIndex = currentPage * rowsPerPage + 1
  const endResultIndex = Math.min(startResultIndex + rowsPerPage - 1, resultsCount)

  const { digestInstructorsList } = useAcademicDataDigest()
  const finalDataTable = useMemo(() => {
    if (pageResults) {
      return digestInstructorsList(pageResults)
    }
  }, [pageResults])
  const rowsCount = size(finalDataTable)

  /**
   * Handle click on external links.
   * @param {React.MouseEvent<HTMLAnchorElement>} e
   */
  const handleLinkClick = (e) => {
    // Prevent opening target link when running in SB.
    if (isStorybook) {
      e.preventDefault()
      alert('Detail page route will open when running in Drupal.')
    }
  }

  /**
   * Custom render function for instructor name column
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderNameLink = (dataIndex) => {
    const name = finalDataTable[dataIndex].name
    const employeeId = finalDataTable[dataIndex].employeeId
    return employeeId ? (
      <Link onClick={handleLinkClick} href={`/instructor-search/instructor/${employeeId}`}>
        {name}
      </Link>
    ) : (
      <>{name}</>
    )
  }

  const tableColumns = [
    {
      name: 'instructorName',
      label: t('Name'),
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderNameLink,
      },
    },
    {
      name: 'taughtSubject',
      label: t('Taught Subject'),
      options: {
        filter: false,
        sort: false,
      },
    },
  ]

  /**
   * @type {MUIDataTableOptions}
   */
  const options = {
    page: Number(currentPageIndex),
    // onChangePage: onPageChange,
    jumpToPage: false,
    pagination: false,
    rowsPerPage,
    textLabels: {
      body: {
        noMatch: loading ? t('Please wait...') : t('No results found'),
      },
    },
  }

  return (
    <Box className="instructor-search">
      {FormInstance.FormComp}
      {loading && (
        <Box
          display={'flex'}
          justifyContent={'start'}
          sx={{
            mt: 2,
          }}
        >
          <>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>{t('Loading...')}</Typography>
          </>
        </Box>
      )}
      {!loading && !isEmpty(currentFilters) && (
        <Box className="instructor-search__results">
          <Box className="instructor-search__results-header">
            <h4 className="instructor-search__results-title">
              {t('Search Results: ({{startIndex}}-{{endIndex}})', {
                startIndex: startResultIndex,
                endIndex: endResultIndex,
              })}
            </h4>
            <Box className="instructor-search__results-filter">
              <SelectOptionControl
                id="page-items"
                options={rowsPerPageOptions}
                defaultValue={`${rowsPerPage}`}
                selectLabel={t('Rows per Page:')}
                onSelectOption={handlers.handleRowsPerPage}
              />
            </Box>
          </Box>
          <Box className="instructor-search__results-table">
            <DataTable
              className="data-table"
              title={''}
              data={finalDataTable}
              columns={tableColumns}
              options={{
                ...options,
                count: rowsCount,
              }}
            />
          </Box>
          <Box className="instructor-search__results-footer">
            <Box className="instructor-search__results-footer-pagination">
              <TablePagination
                component="div"
                count={resultsCount}
                page={currentPage}
                onPageChange={handlers.handleChangePage}
                rowsPerPage={Number(rowsPerPage)}
                rowsPerPageOptions={[]}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
